import React, { useRef, useState } from 'react';
import { Block, RowRemove } from './WrapUpParts';
import { BUTTON_QUIET } from './adminStyles';
import { MAX_REPORT_PHOTOS, placePhotos, type PhotoSeam, type ReportSections, type WrapUpPhoto } from '../../utils/wrapUpPhotos';
import { removeWrapUpPhoto, uploadWrapUpPhoto } from '../../utils/wrapUpPhotoStore';

/**
 * THE PHOTOGRAPHS BLOCK in the wrap-up editor.
 *
 * Tom, 27 Sep: these are "just to make the report look pretty". They are not
 * the client's photography deliverable and not a download set, so this does
 * not pick from the gallery -- the frames worth printing in a report are
 * often grabs and behind-the-scenes shots that were never delivered. Staff
 * upload them, and the browser resizes each one on the way in.
 *
 * IT SAYS WHERE EACH ONE WILL LAND. The point of an automatic arrangement is
 * that nobody has to think about it; the cost is a person uploading eight
 * pictures with no idea what becomes of them. Every row names its
 * destination, worked out by the same function the client page uses, so
 * reordering the list moves the labels with it.
 *
 * Nothing here decides a position. Order in, arrangement out.
 *
 * THE FILE INPUT SITS OUTSIDE `Block`, AND MUST STAY THERE. `Block` renders
 * its children only once the section has content -- that is the whole point
 * of it, an unused section shows "Not included" instead of empty fields. The
 * first version put the hidden input among those children, so on a report
 * with no photographs yet the input did not exist, the ref was null, and
 * "Add photographs" was a button that did nothing at all. Which is exactly
 * the state every new report starts in.
 */

const WHERE: Record<PhotoSeam, string> = {
  opening: 'Opening, full width under the statement',
  afterFilms: 'After the films',
  closing: 'Beside the closing paragraph, on black',
  voices: 'Before the testimonials',
  next: 'Before “Where this could go”'
};
export function WrapUpPhotoBlock({
  projectId,
  photos,
  sections,
  onChange,
  notify
}: {
  projectId: string;
  photos: WrapUpPhoto[];
  sections: ReportSections;
  onChange: (photos: WrapUpPhoto[]) => void;
  notify: (message: string) => void;
}) {
  const picker = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  /* The arrangement the client page will make of exactly this list. */
  const destination = new Map<string, string>();
  for (const placement of placePhotos(photos, sections)) {
    for (const photo of placement.photos) destination.set(photo.url, WHERE[placement.seam]);
  }
  async function add(files: FileList | null) {
    if (!files || files.length === 0) return;
    setBusy(true);
    const added: WrapUpPhoto[] = [];
    for (const file of Array.from(files)) {
      const result = await uploadWrapUpPhoto(projectId, file);
      if (result.ok && result.photo) added.push(result.photo);else notify(result.error ?? 'That photograph could not be saved.');
    }
    setBusy(false);
    if (added.length > 0) {
      onChange([...photos, ...added]);
      notify(added.length === 1 ? 'Photograph added — Save to keep it' : `${added.length} photographs added — Save to keep them`);
    }
  }
  function move(from: number, by: number) {
    const to = from + by;
    if (to < 0 || to >= photos.length) return;
    const next = photos.slice();
    const [lifted] = next.splice(from, 1);
    next.splice(to, 0, lifted);
    onChange(next);
  }
  function drop(index: number) {
    const going = photos[index];
    onChange(photos.filter((_, at) => at !== index));
    void removeWrapUpPhoto(going.path);
  }
  const over = Math.max(0, photos.length - MAX_REPORT_PHOTOS);
  return <>
      {/* Outside Block on purpose — see the note at the top of this file. */}
      <input ref={picker} type="file" accept="image/*" multiple className="hidden" onChange={e => {
      void add(e.target.files);
      e.target.value = '';
    }} data-id="element-1778" data-mp="5nkmhoof72bv" />

      <Block title="Photographs" hint="A handful to carry the report, not the client's gallery. The page decides where each one goes; the order here is the order it uses. Eight at most, and one standing portrait earns its place beside the closing paragraph." filled={photos.length > 0} count={photos.length ? `${photos.length} added` : 'None'} addLabel={busy ? 'Uploading…' : 'Add photographs'} onAdd={() => picker.current?.click()} data-id="element-1779" data-mp="3hjpboo18586c">
        {over > 0 && <p className="mb-6 text-sm leading-relaxed text-paper/70" data-id="element-1780" data-mp="93x7w3rwkwj9">
            The last {over} will not be shown. Eight is the most a report carries before it
            reads as a contact sheet.
          </p>}

        {photos.map((photo, index) => {
        const where = destination.get(photo.url);
        return <div key={photo.url} className="mt-4 flex items-center gap-5 first:mt-0" data-id="element-1781" data-mp="rv6kilwz01f2" data-mpi={index}>
              <img src={photo.url} alt="" className="h-16 w-24 shrink-0 bg-ink object-cover" data-id="element-1782" data-mp="rx1704nw41l9" />
              <div className="min-w-0 flex-grow" data-id="element-1783" data-mp="2cj8wfiav6opf">
                <p className="m-0 text-sm text-paper" data-id="element-1784" data-mp="27lh80y9fhkpz">{where ?? 'Not shown'}</p>
                <p className="mt-1 m-0 text-xs text-paper/35" data-id="element-1785" data-mp="2mbwro8m74km1">
                  {photo.width} &times; {photo.height}
                  {photo.width >= photo.height ? ' landscape' : ' portrait'}
                </p>
              </div>
              <button type="button" onClick={() => move(index, -1)} disabled={index === 0} aria-label={`Move photograph ${index + 1} earlier`} className={`${BUTTON_QUIET} disabled:opacity-25`} data-id="element-1786" data-mp="1tn9dsy3s0xii">
                Up
              </button>
              <button type="button" onClick={() => move(index, 1)} disabled={index === photos.length - 1} aria-label={`Move photograph ${index + 1} later`} className={`${BUTTON_QUIET} disabled:opacity-25`} data-id="element-1787" data-mp="1yo6v78gypniy">
                Down
              </button>
              <RowRemove label={`Remove photograph ${index + 1}`} onClick={() => drop(index)} data-id="element-1788" data-mp="2bo3poqmidj7v" />
            </div>;
      })}
      </Block>
    </>;
}